import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, eur } from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { amdenNav, MODULE_ITEMS } from "@/lib/navConfig";
import { PageHeader, KpiCard } from "@/components/Common";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Building2, Users, MapPin, Pencil, Upload, Loader2, Briefcase, Settings2, Trash2, LogIn, KeyRound, ToggleLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const BLANK = { name: "", slug: "", primary_color: "#0F172A", industry: "", admin_name: "", admin_email: "", admin_password: "" };

export default function Companies() {
  const nav = useNavigate();
  const { enterCompany } = useAuth();
  const [overview, setOverview] = useState(null);
  const [rows, setRows] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [createdLogin, setCreatedLogin] = useState(null);
  const [allLoginsDlg, setAllLoginsDlg] = useState(false);
  const [allLoginsResult, setAllLoginsResult] = useState(null);
  const [runningAll, setRunningAll] = useState(false);
  const fileRefs = useRef({});
  const [modCompany, setModCompany] = useState(null);
  const [modForm, setModForm] = useState({});
  const [formsAuthoring, setFormsAuthoring] = useState("admin");
  const openModules = (c) => { const em = c.enabled_modules || {}; const f = {}; MODULE_ITEMS.forEach((m) => { f[m.to] = em[m.to] !== false; }); setModForm(f); setFormsAuthoring(c.forms_authoring || "admin"); setModCompany(c); };
  const saveModules = async () => {
    try { await api.put(`/companies/${modCompany.id}/modules`, { enabled_modules: modForm, forms_authoring: formsAuthoring }); toast.success("Modules updated — client sees changes on next refresh"); setModCompany(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const load = () => {
    api.get("/companies/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/companies").then((r) => setRows(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/industries").then((r) => setIndustries(r.data)).catch(() => {}); }, []);
  const selectedIndustry = industries.find((i) => i.name === form.industry);

  const openCreate = () => { setEditId(null); setForm(BLANK); setDialog(true); };
  const openEdit = (c) => { setEditId(c.id); setForm({ name: c.name || "", slug: c.slug || "", primary_color: c.primary_color || "#0F172A", admin_name: "", admin_email: "", admin_password: "" }); setDialog(true); };
  const doEnter = async (c) => {
    try { await enterCompany(c.id); toast.success(`Entered ${c.name} — support mode`); nav("/admin"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const runAllLogins = async () => {
    setRunningAll(true);
    try {
      const { data } = await api.post("/companies/create-missing-logins-all");
      setAllLoginsDlg(false);
      setAllLoginsResult(data);
      load();
      toast.success(`Created ${data.totals.created} login${data.totals.created === 1 ? "" : "s"} across ${data.companies} companies`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRunningAll(false); }
  };

  const save = async () => {
    if (!form.name || !form.slug) { toast.error("Name and slug are required"); return; }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/companies/${editId}`, { name: form.name, slug: form.slug, primary_color: form.primary_color });
        toast.success("Company updated");
      } else {
        const res = await api.post("/companies", form);
        toast.success("Company onboarded");
        if (res.data?.admin_login) setCreatedLogin(res.data.admin_login);
      }
      setDialog(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (c) => {
    try { await api.put(`/companies/${c.id}/active`, { active: !c.active }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/companies/${confirmDel.id}`); toast.success("Company deleted"); setConfirmDel(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onLogo = async (c, file) => {
    if (!file) return;
    setUploadingId(c.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/companies/${c.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploadingId(null); }
  };

  return (
    <AdminLayout nav={amdenNav} title="Companies">
      <PageHeader title="Companies" subtitle="Onboard and manage every client company on the platform">
        <div className="flex gap-2">
          <Button data-testid="create-logins-all-btn" variant="outline" onClick={() => setAllLoginsDlg(true)}><KeyRound className="h-4 w-4 mr-1" />Create staff logins (all)</Button>
          <Button data-testid="new-company-btn" onClick={openCreate} className="bg-slate-900 text-white">+ Onboard company</Button>
        </div>
      </PageHeader>

      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <KpiCard testId="ov-companies" label="Active Companies" value={`${overview.active_companies}/${overview.total_companies}`} icon={Building2} accent />
          <KpiCard testId="ov-users" label="Active Users" value={overview.active_users} icon={Users} />
          <KpiCard testId="ov-employees" label="Employees" value={overview.total_employees} icon={Briefcase} />
          <KpiCard testId="ov-sites" label="Active Sites" value={overview.active_sites} icon={MapPin} />
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((c) => (
          <div key={c.id} data-testid={`company-card-${c.id}`} className="bg-white border border-zinc-200 rounded-lg p-5">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-md flex items-center justify-center overflow-hidden border border-zinc-200" style={{ backgroundColor: c.primary_color || "#0F172A" }}>
                  {c.logo_url
                    ? <BrandLogo company={c} className="h-9 max-w-[44px]" />
                    : <span className="text-white font-bold text-sm" data-testid={`company-initials-${c.id}`}>{(c.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}</span>}
                </div>
                <div>
                  <div className="font-heading font-bold text-slate-900 leading-tight">{c.name}</div>
                  <div className="text-xs text-zinc-400 font-mono">/{c.slug}</div>
                </div>
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${c.active ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>{c.active ? "Active" : "Disabled"}</span>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4 text-center">
              <Stat label="Users" value={c.stats?.active_users ?? 0} />
              <Stat label="Employees" value={c.stats?.employees ?? 0} />
              <Stat label="Sites" value={c.stats?.sites ?? 0} />
            </div>

            <div className="mt-4 pt-4 border-t border-zinc-100 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <input ref={(el) => (fileRefs.current[c.id] = el)} type="file" accept="image/*" className="hidden" data-testid={`logo-input-${c.id}`} onChange={(e) => onLogo(c, e.target.files?.[0])} />
                <Button data-testid={`upload-logo-${c.id}`} size="sm" variant="outline" className="h-8" disabled={uploadingId === c.id} onClick={() => fileRefs.current[c.id]?.click()}>
                  {uploadingId === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Upload className="h-3.5 w-3.5 mr-1" />Logo</>}
                </Button>
                <Button data-testid={`edit-company-${c.id}`} size="sm" variant="outline" className="h-8" onClick={() => openEdit(c)}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
                <Button data-testid={`manage-company-${c.id}`} size="sm" className="h-8 bg-slate-900 text-white" onClick={() => nav(`/amden/companies/${c.id}`)}><Settings2 className="h-3.5 w-3.5 mr-1" />Manage</Button>
                <Button data-testid={`modules-company-${c.id}`} size="sm" variant="outline" className="h-8" onClick={() => openModules(c)}><ToggleLeft className="h-3.5 w-3.5 mr-1" />Modules</Button>
                <Button data-testid={`enter-company-${c.id}`} size="sm" variant="outline" className="h-8 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => doEnter(c)}><LogIn className="h-3.5 w-3.5 mr-1" />Enter</Button>
                <Button data-testid={`delete-company-${c.id}`} size="sm" variant="outline" className="h-8 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setConfirmDel(c)}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-500">Company access</span>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold ${c.active ? "text-emerald-600" : "text-zinc-400"}`} data-testid={`company-access-label-${c.id}`}>{c.active ? "Enabled" : "Disabled"}</span>
                  <Switch data-testid={`toggle-company-${c.id}`} checked={!!c.active} onCheckedChange={() => toggleActive(c)} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!modCompany} onOpenChange={(v) => !v && setModCompany(null)}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto kqm-scroll">
          <DialogHeader>
            <DialogTitle>Modules — {modCompany?.name}</DialogTitle>
            <DialogDescription>Turn features on or off for this client. Disabled modules are hidden from their admin menu and blocked. Dashboard and Settings are always available.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            {MODULE_ITEMS.map((m) => (
              <div key={m.to} className="flex items-center justify-between py-2 border-b border-zinc-50">
                <span className="text-sm font-medium">{m.label}</span>
                <Switch data-testid={`mod-${m.to.replace(/[^a-z]+/gi, "-")}`} checked={modForm[m.to] !== false} onCheckedChange={(v) => setModForm((f) => ({ ...f, [m.to]: v }))} />
              </div>
            ))}
          </div>
          {modForm["/admin/forms"] !== false && (
            <div className="mt-3 border border-zinc-200 rounded-md p-3" data-testid="forms-authoring">
              <div className="text-sm font-medium mb-2">Forms & Checklists: who builds forms?</div>
              {[["admin", "Admin builds", "Company admins and managers create and edit forms"], ["amden", "Amden builds", "Only Amden Super Admins create forms; admins view, see submissions and resolve fails"]].map(([v, l, d]) => (
                <label key={v} className={`flex items-start gap-2 p-2 rounded-md cursor-pointer ${formsAuthoring === v ? "bg-amber-50" : ""}`}>
                  <input type="radio" name="forms-authoring" data-testid={`forms-authoring-${v}`} checked={formsAuthoring === v} onChange={() => setFormsAuthoring(v)} className="mt-1 accent-amber-500" />
                  <span><span className="text-sm font-semibold block">{l}</span><span className="text-xs text-zinc-500">{d}</span></span>
                </label>
              ))}
            </div>
          )}
          <Button data-testid="mod-save" onClick={saveModules} className="w-full bg-slate-900 text-white mt-3">Save modules</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit company" : "Onboard new company"}</DialogTitle>
            <DialogDescription>{editId ? "Update this client's name, slug and brand colour." : "Set up a new client company. Pick an industry to pre-fill its starter job roles and pay rates."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Company name</Label><Input data-testid="c-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Slug (login URL)</Label><Input data-testid="c-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.replace(/[^a-z0-9-]/gi, "").toLowerCase() })} placeholder="brightspark" className="mt-1" /><p className="text-[11px] text-zinc-400 mt-1">Branded login at /login/{form.slug || "slug"}</p></div>
            <div><Label>Brand colour</Label><div className="flex items-center gap-2 mt-1"><input type="color" data-testid="c-color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="h-9 w-14 rounded border border-zinc-200" /><span className="text-xs font-mono text-zinc-500">{form.primary_color}</span></div></div>
            {!editId && (
              <div>
                <Label>Industry (optional)</Label>
                <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
                  <SelectTrigger data-testid="c-industry" className="mt-1"><SelectValue placeholder="Pick an industry to pre-fill job roles" /></SelectTrigger>
                  <SelectContent>
                    {industries.map((ind) => (
                      <SelectItem key={ind.name} value={ind.name} data-testid={`c-industry-opt-${ind.name}`}>{ind.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedIndustry
                  ? <p className="text-[11px] text-emerald-600 mt-1" data-testid="c-industry-preview">{selectedIndustry.roles.length} starter job roles will be created: {selectedIndustry.roles.map((r) => r.name).join(", ")}</p>
                  : <p className="text-[11px] text-zinc-400 mt-1">Leave blank to start with the default electrical roles.</p>}
              </div>
            )}
            {!editId && (
              <div className="border-t border-zinc-100 pt-3 space-y-3">
                <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">First administrator <span className="normal-case font-normal text-zinc-400">(optional — a login is auto-created if left blank)</span></div>
                <div><Label>Admin name</Label><Input data-testid="c-admin-name" value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} className="mt-1" /></div>
                <div><Label>Admin email</Label><Input data-testid="c-admin-email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} className="mt-1" /></div>
                <div><Label>Admin password</Label><Input data-testid="c-admin-password" type="text" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} className="mt-1" /></div>
              </div>
            )}
            <Button data-testid="c-submit" onClick={save} disabled={saving} className="w-full bg-slate-900 text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editId ? "Save changes" : "Onboard company"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!createdLogin} onOpenChange={(v) => !v && setCreatedLogin(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Admin login ready</DialogTitle>
            <DialogDescription>{createdLogin?.generated ? "This password was auto-generated — copy it now, it won't be shown again. You can now Enter the company or share these with the client's admin." : "Share these credentials with the client's administrator, or Enter the company to configure it."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between gap-2 rounded-md border border-zinc-200 p-2">
              <span className="text-zinc-500">Email</span>
              <span className="font-mono text-slate-900 truncate" data-testid="created-admin-email">{createdLogin?.email}</span>
            </div>
            {createdLogin?.password && (
              <div className="flex items-center justify-between gap-2 rounded-md border border-zinc-200 p-2">
                <span className="text-zinc-500">Password</span>
                <span className="font-mono text-slate-900" data-testid="created-admin-password">{createdLogin.password}</span>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" data-testid="copy-admin-login" onClick={() => { navigator.clipboard?.writeText(`${createdLogin.email}${createdLogin.password ? " / " + createdLogin.password : ""}`); toast.success("Copied"); }}>Copy</Button>
            <Button className="bg-slate-900 text-white" data-testid="close-admin-login" onClick={() => setCreatedLogin(null)}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={allLoginsDlg} onOpenChange={setAllLoginsDlg}>
        <DialogContent className="max-w-md" data-testid="create-logins-all-confirm">
          <DialogHeader>
            <DialogTitle>Create staff logins across all companies?</DialogTitle>
            <DialogDescription>For every active company, this creates an employee login for anyone with an email and no login yet, using their employee number as the password. Existing logins, and employees with no email or no employee number, are skipped. Nothing existing is changed.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" data-testid="create-logins-all-cancel" onClick={() => setAllLoginsDlg(false)}>Cancel</Button>
            <Button className="bg-slate-900 text-white" data-testid="create-logins-all-go" disabled={runningAll} onClick={runAllLogins}>{runningAll ? "Creating…" : "Create logins"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!allLoginsResult} onOpenChange={(v) => !v && setAllLoginsResult(null)}>
        <DialogContent className="max-w-lg" data-testid="create-logins-all-result">
          <DialogHeader>
            <DialogTitle>Staff logins created</DialogTitle>
            <DialogDescription>Each new login signs in with their email and their employee number as the password.</DialogDescription>
          </DialogHeader>
          {allLoginsResult && (
            <div className="space-y-3 text-sm">
              <p><strong data-testid="all-created-count">{allLoginsResult.totals.created}</strong> login{allLoginsResult.totals.created === 1 ? "" : "s"} created across {allLoginsResult.companies} companies. Skipped {allLoginsResult.totals.skipped_existing} existing, {allLoginsResult.totals.skipped_no_email} with no email, {allLoginsResult.totals.skipped_no_number} with no employee number.</p>
              <div className="max-h-64 overflow-y-auto border border-zinc-200 rounded-md kqm-scroll" data-testid="all-per-company">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-zinc-500 border-b border-zinc-100"><th className="p-2">Company</th><th className="p-2 text-right">Created</th><th className="p-2 text-right">Skipped</th></tr></thead>
                  <tbody>
                    {allLoginsResult.per_company.map((r) => (
                      <tr key={r.company_id} className="border-b border-zinc-50">
                        <td className="p-2 font-medium">{r.company_name}</td>
                        <td className="p-2 text-right num">{r.created}</td>
                        <td className="p-2 text-right num text-zinc-400">{r.skipped_existing + r.skipped_no_email + r.skipped_no_number}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete {confirmDel?.name}?</DialogTitle>
            <DialogDescription>This permanently removes the company and all of its data. This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-zinc-600">This permanently removes the company and <strong>all of its data</strong> — users, employees, sites, timesheets, materials, leave and settings. This cannot be undone.</p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button data-testid="confirm-delete-company" className="bg-rose-600 text-white" onClick={doDelete}>Delete permanently</Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-zinc-50 rounded-md py-2">
      <div className="num font-bold text-slate-900">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-400">{label}</div>
    </div>
  );
}
